import React from 'react';
import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';

/**
 * Adding a station, and editing one.
 *
 * One dialog for both, the way the printer one works. A station is only a
 * name — it deliberately carries no printer and no expense category, so there
 * is nothing else to ask for.
 *
 * There is no Active switch. A station is retired with the delete action,
 * which is what the server does with it, and bringing one back is a thing
 * nobody has asked for yet.
 */

const COPY = {
  addTitle: 'New Station',
  editTitle: 'Edit Station',
  name: 'Name (example Kitchen)',
  nameRequired: 'Enter a station name',
  add: 'Add Station',
  save: 'Save',
  failed: 'Could not save. Try again.',
  note: 'Stock issued with the Usage reason is posted to a station, and the reports split cost and revenue by it.',
};

export default function StationDialog({ station, businessId, onClose, onSubmit }) {
  const editing = Boolean(station);

  const [name, setName] = React.useState(() => (station && station.name) || '');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    if (!name.trim()) {
      setError(COPY.nameRequired);
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        station,
        payload: { name: name.trim(), business: businessId },
      });
      onClose();
    } catch (err) {
      // The server's own sentence: a duplicate name comes back as one, and it
      // says more than anything invented here.
      setError(err?.response?.data?.message || err.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editTitle : COPY.addTitle}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 18 }}>
          <PillField
            name="station-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.name}
            disabled={busy}
            invalid={Boolean(error)}
            autoFocus
          />
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.save : COPY.add}
        </PrimaryButton>

        <div
          style={{
            fontSize: 11.5,
            color: T.inkFaint,
            lineHeight: 1.5,
            textAlign: 'center',
            paddingTop: 14,
          }}
        >
          {COPY.note}
        </div>
      </div>
    </Dialog>
  );
}
